import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { serverError } from "../../../../components/modals/payments/register/registerPaymentModal";
import TiraDeMeses from "../../../../components/acuerdos/tiraDeMeses";
import PerfilAlumnoModal from "../../../academy/perfilAlumnoModal";
import ExpedienteLimpieza, { ListaDeNotas, fecha } from "./expedienteLimpieza";
import VinculoLimpieza from "./vinculoLimpieza";

/*
	Descuentos › Limpieza (Etapa 5 de cobros). Las matrículas activas que nacieron antes de los acuerdos: WAPP lee lo YA
	PAGADO de cada una y propone la promoción del catálogo por dos caminos, lado a lado: las Reglas (reconocen el patrón de lo
	pagado) y Sonnet (lee pagos, nota de matrícula, solicitud y contrato; `acuerdos:limpieza --ia`). Decisión de Esteban
	(2026-10-20): Caja del plantel aplica UNA POR UNA (ya no existe confirmar en bloque), siempre viendo antes y después mes por
	mes (simulado en el servidor). Nada sube de precio solo; lo pagado no se toca; lo que nadie decide sigue «Por revisar».
	Todo lo valida el servidor.
	2026-10-27 (Esteban: «difícil de comprender»): UNA sola sugerencia (la de Sonnet si ya leyó el caso, si no la de las reglas),
	una sola medida (Clara / Revisar / Dudosa) en vez de confianzas y «coincide/difiere», las notas del perfil, pagos y facturas
	en la tarjeta, Perfil y Estado de Cuenta en ventana, y las matrículas más recientes primero. Sin «Rechazar» (Esteban, 2026-10-27: duplicaba
	«Sin Promoción»; si lo prometido no está en el catálogo, se crea la promo y se elige con «Elegir Otra»; si no se sabe, el caso se deja «Por Revisar»).
*/

const http = HttpManager.getInstance();
const num = (n: number | null | undefined) => n === null || n === undefined ? "—" : (Math.round(Number(n) * 100) / 100).toLocaleString("es-MX", { maximumFractionDigits: 2 });
const CERTEZA: Record<string, { label: string; chip: string; ayuda: string }> = {
	clara: { label: "Clara", chip: "wdChipAcento", ayuda: "Lo pagado cuadra con la promoción y nada se contradice" },
	revisar: { label: "Revisar", chip: "wdChipOro", ayuda: "Cuadra, pero hay poca evidencia o algo que conviene mirar" },
	dudosa: { label: "Dudosa", chip: "wdChipDudosa", ayuda: "Lo pagado, las notas o las sugerencias se contradicen" }
};
const FUENTE: Record<string, string> = {
	ambos: "Sonnet leyó el caso y las reglas dicen lo mismo",
	sonnet: "Sonnet leyó el caso",
	reglas: "Sale solo de lo pagado (Sonnet todavía no lee este caso)"
};

type Modo = "ia" | "confirmar" | "otra" | "ninguna" | "rechazar";

const estadoTira = (m: any) => m.state === "pagado" || m.state === "abono" ? "pagado" : m.pct !== null && m.pct >= 99.5 ? "gratis" : m.pct !== null && m.pct >= 0.5 ? "descuento" : "pendiente";

/*
	soloAlumno (Esteban, 2026-11-02): la Limpieza de UNA persona dentro de una ventana (Estado de Cuenta, Registrar Pago), para revisar y
	decidir su promoción ahí mismo antes de cobrar. Sin filtros ni páginas; onDecidido avisa para recargar lo de atrás.
*/
const Limpieza: React.FC<{ campusId: number; sym: string; soloAlumno?: { studentId: number; enrollmentId?: number }; onDecidido?: () => void }> = ({ campusId, sym: symProp, soloAlumno, onDecidido }) => {
	const [data, setData] = useState<any>(null);
	const sym = symProp || data?.currencySymbol || "";
	const [error, setError] = useState<string | null>(null);
	const [f, setF] = useState<{ status: string; certeza: string; orden: string; q: string }>({ status: soloAlumno ? "todos" : "1", certeza: "", orden: "", q: "" });
	const [ventana, setVentana] = useState<{ tipo: "perfil" | "cuenta"; row: any } | null>(null);
	const [abierto, setAbierto] = useState<any>(null);
	const [dec, setDec] = useState<{ decision: Modo | null; promotionId: number | null; notes: string; subir: boolean; links: number[]; linkOk: boolean; alReves: boolean; otra: any; juntas: any[] }>(
		{ decision: null, promotionId: null, notes: "", subir: false, links: [], linkOk: false, alReves: false, otra: null, juntas: [] });
	const [busy, setBusy] = useState(false);
	const [cargando, setCargando] = useState<number | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [page, setPage] = useState<number>(1);
	const arriba = useRef<HTMLDivElement>(null);

	const load = () => {
		http.paymentService.cleanup(campusId, soloAlumno ? { ...f, studentId: soloAlumno.studentId, enrollmentId: soloAlumno.enrollmentId } : f, page).then((r: any) => { setData(r.data); setError(null); }).catch((e: any) => setError(serverError(e)));
	};
	useEffect(() => {
		const t = setTimeout(load, 250);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, f.status, f.certeza, f.orden, f.q, page]);
	useEffect(() => { setAbierto(null); }, [campusId, f.status, f.certeza, f.orden, f.q, page]);
	//Otro filtro = empezar en la primera página.
	useEffect(() => { setPage(1); }, [campusId, f.status, f.certeza, f.orden, f.q]);

	/* Abre el caso con el modo elegido (o solo a mirar si modo es null). La vista previa se simula con la promoción de ese modo. */
	const abrir = async (row: any, modo: Modo | null) => {
		if (abierto && abierto.id === row.id && dec.decision === modo) { setAbierto(null); return; }
		const promotionId = modo === "ia" ? row.ai?.promotionId : modo === "confirmar" ? row.promotionId : null;
		try {
			setCargando(row.id);
			setError(null);
			const r = await http.paymentService.cleanupItem(campusId, row.id, promotionId || null);
			setAbierto(r.data);
			setDec({ decision: modo, promotionId: null, notes: "", subir: false, links: [], linkOk: false, alReves: false, otra: null, juntas: [] });
		} catch (e) {
			setError(serverError(e));
		} finally {
			setCargando(null);
		}
	};
	/* En «Elegir Otra», al escoger una promoción se pide su vista previa. */
	const previa = async (row: any, promotionId: number | null) => {
		setDec((d) => ({ ...d, promotionId, subir: false, links: [], linkOk: false, alReves: false, otra: null, juntas: [] }));
		if (!promotionId) return;
		try {
			setCargando(row.id);
			const r = await http.paymentService.cleanupItem(campusId, row.id, promotionId);
			setAbierto(r.data);
		} catch (e) {
			setError(serverError(e));
		} finally {
			setCargando(null);
		}
	};

	const decidir = async () => {
		if (!abierto || !dec.decision) return;
		const pv = abierto.preview;
		const conPromo = dec.decision === "ia" || dec.decision === "confirmar" || dec.decision === "otra";
		const alReves = conPromo && dec.alReves && dec.otra;
		const txt = alReves ? "Paga completo (ningún mes suyo cambia). A " + dec.otra.name + " se le aplica " + (pv?.promoName ? "«" + pv.promoName + "»" : "la promoción")
				+ " ligada a esta matrícula. Lo ya pagado no cambia."
			: dec.decision === "ninguna" ? "Se queda sin promoción; ningún precio cambia."
			: dec.decision === "rechazar" ? "Se rechaza la sugerencia; Caja sigue poniendo el descuento a mano."
			: "Se aplica " + (pv?.promoName ? "«" + pv.promoName + "»" : "la promoción") + (pv ? ": " + pv.down + (pv.down === 1 ? " mes queda más barato" : " meses quedan más baratos")
				+ (pv.up ? (dec.subir ? " y " + pv.up + " SUBEN (queda en Auditoría)" : "; " + pv.up + " que subirían se quedan como están") : "") : "") + ". Lo ya pagado no cambia.";
		// Las demás tarjetas que la promo une se deciden a la vez (el servidor hace el mismo plan que se vio).
		const juntas = conPromo && !alReves ? dec.juntas.filter((t: any) => t.action === "promo" || t.action === "sinPromo") : [];
		const mas = juntas.map((t: any) => "\n" + t.name + ": " + (t.action === "sinPromo" ? "queda como base: paga completo, ningún mes cambia." : "también lleva " + (pv?.promoName ? "«" + pv.promoName + "»" : "la promoción") + "."));
		if (!window.confirm(abierto.name + ": " + txt + mas.join(""))) return;
		try {
			setBusy(true);
			const r = await http.paymentService.cleanupDecide(campusId, abierto.id, { decision: dec.decision, promotionId: dec.decision === "otra" ? dec.promotionId : null,
				notes: dec.decision === "ia" ? "" : dec.notes.trim(), permitirSubir: conPromo && !alReves ? dec.subir : false, links: conPromo ? dec.links : [], alReves: !!alReves });
			setAviso(abierto.name + ": " + r.data.text);
			setAbierto(null);
			setError(null);
			load();
			if (onDecidido) onDecidido();
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};
	const releer = async () => {
		try { setBusy(true); await http.paymentService.cleanupRefresh(campusId); load(); }
		catch (e) { setError(serverError(e)); }
		finally { setBusy(false); }
	};

	if (!data) return error ? <div className="wdAviso">{error}</div> : <div className="loader" />;
	const c = data.counts;
	const sugerencia = (r: any) => {
		const g = r.suggestion || {};
		const ct = CERTEZA[r.certeza] || CERTEZA.revisar;
		const otra = g.other;
		return (
			<div className="wdSug">
				<div className="wdSugPromo">
					<span className="wdTenue" style={{ fontWeight: 400 }}>WAPP sugiere:</span> {g.promoName || "Nada claro todavía"}
					<span className={"wdChip " + ct.chip} title={ct.ayuda}>{ct.label}</span>
				</div>
				{g.reason ? <div className="wdSugRazon">{g.reason}</div> : null}
				{g.warnings && g.warnings.length ? <ul className="wdBanderas">{g.warnings.map((x: string, i: number) => <li key={"b" + i}>{x}</li>)}</ul> : null}
				{otra ? <div className="wdSugRazon wdTenue">Las reglas (solo lo pagado) proponían «{otra.promoName || "Sin Promoción"}».</div> : null}
				{r.group ? <div className="wdSugRazon wdOro">{r.group.text}</div> : null}
				<div className="wdSugFuente">{FUENTE[g.source] || ""}{r.aiError ? " · Sonnet no pudo leerlo: " + r.aiError : ""}</div>
			</div>
		);
	};
	const notasCortas = (r: any) => {
		// La nota de la matrícula suele repetirse en su historial: se enseña una vez.
		const limpio = (t: string) => (t || "").replace(/\s+/g, " ").trim().toLowerCase();
		const base = limpio(r.noteText);
		const otras = (r.notes || []).filter((n: any) => !base || base.indexOf(limpio(n.text)) < 0);
		const lista = [...(r.noteText ? [{ source: "Matrícula y Solicitud", text: r.noteText, at: null }] : []), ...otras];
		const total = (r.noteText ? 1 : 0) + Math.max(0, (r.notesTotal || 0) - ((r.notes || []).length - otras.length));
		return (
			<div className="wdSug wdSugNotas">
				<div className="wdSugTit">Notas{total ? " (" + total + ")" : ""}</div>
				<ListaDeNotas notas={lista.slice(0, 4)} vacio="Nadie escribió notas en el perfil, la matrícula, los pagos ni las facturas." />
				{total > 4 ? <button type="button" className="wdLink" style={{ fontSize: 13 }} onClick={() => setVentana({ tipo: "cuenta", row: r })}>Ver las {total} notas</button> : null}
			</div>
		);
	};

	/* Lo que pasa con cada mes, en palabras de Caja (antes: «Baja», «Sube», «Igual»). */
	const queCambia = (x: any) => {
		if (x.effect === "pagado") return "Ya está pagado: no cambia";
		if (x.effect === "subiria") return dec.subir ? "Sube a " + sym + num(x.afterIfRaise) : "Subiría " + sym + num((x.afterIfRaise ?? 0) - (x.before ?? 0)) + ": se queda como está, salvo que lo marques abajo";
		// Regalo que espera el pago adelantado (multimes, 2x1, reingreso): se cobra normal hasta entonces; lo dice la línea de arriba.
		if (x.effect !== "baja" && (x.state === "condicionado" || x.state === "regalo" || x.state === "regalo_parcial")) return "Queda gratis al cumplirse la condición de arriba";
		if (x.effect === "baja" && (x.after ?? 0) <= 0.005) return "Queda gratis";
		if (x.effect === "baja") return "Queda " + sym + num((x.before ?? 0) - (x.after ?? 0)) + " más barato";
		if (x.effect === "igual") return "Sin cambio";
		return x.effect;
	};

	const tablaPrevia = (pv: any) => (
		<div className="wdTablaCaja">
			<table className="wdTabla">
				<thead><tr><th>Mes</th><th>Precio Normal</th><th>Cobra Hoy</th><th>Con la Promoción</th><th>Qué Cambia</th></tr></thead>
				<tbody>{pv.months.map((x: any) => (
					<tr key={"pm" + x.tuitionId}>
						<td>{x.title}</td><td>{sym}{num(x.list)}</td><td>{sym}{num(x.before)}</td>
						<td className={x.effect === "subiria" ? "wdOro" : ""}>{sym}{num(x.effect === "subiria" && dec.subir ? x.afterIfRaise : x.after)}</td>
						<td className={x.effect === "subiria" ? "wdOro" : "wdTenue"}>{queCambia(x)}
							{x.note && x.effect !== "pagado" && x.effect !== "subiria" ? <span className="wdTenue"> {x.note}</span> : null}</td>
					</tr>
				))}</tbody>
			</table>
		</div>
	);
	const detalle = () => {
		const a = abierto;
		const pv = a.preview;
		const m = dec.decision;
		const opciones = (a.options || []);
		const puede = a.statusId === 1 && data.canDecide;
		// La promo que se va a aplicar en este modo y si pide «¿Con quién va?».
		const pidAplicar = m === "ia" ? a.ai?.promotionId : m === "confirmar" ? a.promotionId : m === "otra" ? dec.promotionId : null;
		const promoAplicar = pidAplicar ? opciones.find((o: any) => o.id === pidAplicar) : null;
		const pideVinculo = !!(puede && promoAplicar && promoAplicar.linkType);
		// Con promo ligada, la vista previa de esta tarjeta va junto a las demás en «Así Queda Cada Una».
		const verPrevia = pv && m !== "ninguna" && m !== "rechazar" && !(m === "otra" && !dec.promotionId) && !dec.alReves && !pideVinculo;
		const nJuntas = 1 + dec.juntas.filter((t: any) => t.action === "promo" || t.action === "sinPromo").length;
		const casillaSubir = puede && m && pv && pv.up > 0 && !dec.alReves && (verPrevia || pideVinculo) ? (
			<label className="wdOpcion wdOro"><input type="checkbox" checked={dec.subir} onChange={(e) => setDec({ ...dec, subir: e.target.checked })} />
				Sí, que suban {pv.up === 1 ? "el mes marcado" : "los " + pv.up + " meses marcados"}{pideVinculo ? " de " + (a.name || "").split(" ")[0] : ""} (lo decido yo; queda en Auditoría)</label>
		) : null;
		const pideNota = m === "rechazar" || m === "otra";
		// Grey button says what is missing, in plain sight (hover doesn't exist on phones). Esteban, 2026-11-02.
		const letras = dec.notes.trim().length;
		const falta = m === "otra" && !dec.promotionId ? "Elige la promoción arriba."
			: pideNota && letras < 10 ? (letras ? "Al porqué le faltan " + (10 - letras) + (10 - letras === 1 ? " letra." : " letras.") : "Escribe el porqué en la casilla de arriba para poder aplicar.")
				: pideVinculo && !dec.linkOk ? "Falta elegir con quién va la promoción."
					: null;
		return (
			<div className="wdDetalle" onClick={(e) => e.stopPropagation()}>
				{m === "otra" ? (
					<select className="form-select form-select-sm wdElegir" value={dec.promotionId || ""} onChange={(e) => previa(a, Number(e.target.value) || null)}>
						<option value="">Elige la Promoción</option>
						{opciones.map((o: any) => <option key={o.id} value={o.id} disabled={!!o.problem}>{o.name}{o.problem ? " — " + o.problem : o.warning ? " — " + o.warning : ""}</option>)}
					</select>
				) : null}
				{verPrevia ? (
					<>
						<div style={{ fontSize: 13, marginTop: 6 }}>Así quedaría con <b>{pv.promoName}</b>{pv.conditions?.text ? ": " + pv.conditions.text : ""}</div>
						{tablaPrevia(pv)}
					</>
				) : null}
				{verPrevia ? casillaSubir : null}
				{pideVinculo ? (
					<VinculoLimpieza key={a.id + "-" + pidAplicar} campusId={campusId} cleanupId={a.id} promotionId={pidAplicar} linkType={promoAplicar.linkType}
						groupSize={promoAplicar.groupSize} promoName={promoAplicar.name} nombre={(a.name || "").split(" ")[0]} nombreCompleto={a.name || ""} cursoEsta={a.courseName}
						previaEsta={pv} sym={sym} tabla={tablaPrevia}
						onChange={(links, ok, alReves, otra, juntas) => setDec((d) => ({ ...d, links, linkOk: ok, alReves, otra, juntas, subir: alReves ? false : d.subir }))} />
				) : null}
				{pideVinculo ? casillaSubir : null}
				{puede && m ? (
					<div className="wdDecidir" style={{ flexDirection: "column", alignItems: "stretch" }}>
						{m !== "ia" ? (
							<input className="form-control form-control-sm" style={{ maxWidth: 520, flex: "0 0 auto" }} value={dec.notes} onChange={(e) => setDec({ ...dec, notes: e.target.value })}
								placeholder={pideNota ? "Por qué (obligatorio, queda en el historial)" : "Nota (opcional)"} />
						) : null}
						{error ? <div className="wdAviso" style={{ margin: 0 }}>{error}</div> : null}
						<div className="wdBotones" style={{ justifyContent: "flex-start" }}>
							<button type="button" className="wdBoton" onClick={() => setAbierto(null)}>Cerrar</button>
							<button type="button" className="wdBoton wdPrimario" onClick={decidir}
								disabled={busy || !!falta}>
								{busy ? "…" : m === "ninguna" ? "Dejar Sin Promoción" : m === "rechazar" ? "Rechazar"
									: pideVinculo && dec.alReves && dec.otra ? "Aplicar a " + dec.otra.name + " · " + (a.name || "").split(" ")[0] + " paga completo"
									: pideVinculo && nJuntas > 1 ? "Aplicar a las " + nJuntas : "Aplicar " + (pv?.promoName || "")}</button>
							{falta && !busy ? <span className="wdOro" style={{ fontSize: 13, alignSelf: "center" }}>{falta}</span> : null}
						</div>
					</div>
				) : !puede ? (
					<>
						{a.statusId !== 1 ? (
							<div className="wdTenue" style={{ fontSize: 13, marginTop: 6 }}>{a.status}{a.decidedBy ? " por " + a.decidedBy : ""}{a.decidedAt ? ", " + a.decidedAt.replace("T", " ").slice(0, 16) : ""}
								{a.decisionPromoName ? ". " + a.decisionPromoName : ""}{a.decisionNotes ? ". «" + a.decisionNotes + "»" : ""}</div>
						) : null}
						<div className="wdBotones" style={{ justifyContent: "flex-start" }}><button type="button" className="wdBoton" onClick={() => setAbierto(null)}>Cerrar</button></div>
					</>
				) : (
					<>
						{error ? <div className="wdAviso">{error}</div> : null}
						<div className="wdBotones" style={{ justifyContent: "flex-start" }}><button type="button" className="wdBoton" onClick={() => setAbierto(null)}>Cerrar</button></div>
					</>
				)}
			</div>
		);
	};

	const cuenta = (n: number | undefined) => " (" + (n || 0) + ")";
	const sel = (r: any, m: Modo) => abierto && abierto.id === r.id && dec.decision === m;
	// ‹ 1 / N › arriba y al final de los 100 casos; desde abajo, la página nueva se ve desde su primer caso.
	const irA = (n: number, abajo: boolean) => { setPage(n); if (abajo && arriba.current) arriba.current.scrollIntoView({ block: "start" }); };
	const paginas = (abajo: boolean) => data.lastPage > 1 ? (
		<div ref={abajo ? undefined : arriba} className="wdPaginas" style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end", margin: "6px 0" }}>
			<button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => irA(page - 1, abajo)} aria-label="Página anterior">‹</button>
			<span style={{ fontSize: 13 }}>{data.page} / {data.lastPage}</span>
			<button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= data.lastPage} onClick={() => irA(page + 1, abajo)} aria-label="Página siguiente">›</button>
		</div>
	) : null;

	return (
		<>
			{soloAlumno ? null : <>
			<div className="wdFiltros">
				<span className="wdChip wdChipOro">{c.porRevisar} Por Revisar</span>
				{c.porRevisar ? <span className="wdTenue" style={{ fontSize: 13 }}>{c.clara || 0} claras · {c.revisar || 0} por revisar · {c.dudosa || 0} dudosas</span> : null}
			</div>
			{c.porRevisar && c.iaSinAnalisis >= c.porRevisar ? (
				<div className="wdAviso">Sonnet todavía no ha leído los casos de este plantel. Mientras tanto, cada sugerencia sale solo de lo pagado (las reglas),
					sin leer las notas; revisa las notas de la tarjeta antes de aplicar.</div>
			) : c.iaSinAnalisis ? <div className="wdTenue" style={{ fontSize: 13, marginBottom: 6 }}>Sonnet aún no lee {c.iaSinAnalisis} de estos casos; en esos la sugerencia sale solo de lo pagado.</div> : null}
			<div className="wdFiltros">
				<select className="form-select form-select-sm" style={{ maxWidth: 190 }} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} aria-label="Estado">
					<option value="1">Por Revisar</option><option value="2">Aplicadas</option><option value="3">Sin Promoción</option><option value="5">Ya No Aplican</option><option value="todos">Todas</option>
				</select>
				<select className="form-select form-select-sm" style={{ maxWidth: 220 }} value={f.certeza} onChange={(e) => setF({ ...f, certeza: e.target.value })} aria-label="Qué tan clara">
					<option value="">Todas: Claras y Dudosas</option>
					<option value="clara">{"Solo Claras" + cuenta(c.clara)}</option><option value="revisar">{"Solo por Revisar" + cuenta(c.revisar)}</option><option value="dudosa">{"Solo Dudosas" + cuenta(c.dudosa)}</option>
				</select>
				<select className="form-select form-select-sm" style={{ maxWidth: 230 }} value={f.orden} onChange={(e) => setF({ ...f, orden: e.target.value })} aria-label="Orden">
					<option value="">Más Recientes Primero</option><option value="antiguas">Más Antiguas Primero</option>
				</select>
				<input className="form-control form-control-sm" placeholder="Buscar alumno, correlativo o curso" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
				{data.canDecide ? <button type="button" className="wdLink" disabled={busy} onClick={releer} title={data.inferredAt ? "Leído " + fecha(data.inferredAt, true) : ""}>Volver a Leer lo Pagado</button> : null}
			</div>
			</>}
			<div className="wdTenue" style={{ fontSize: 12, marginBottom: 4 }}>Meses: <b>✓</b> ya pagado · <span className="wdMuestra wdMuestraDesc" /> trae descuento escrito, sin pagar · <span className="wdMuestra" /> pendiente a precio normal. Pasa el cursor sobre un mes para ver el detalle.</div>
			{error && !abierto ? <div className="wdAviso">{error}</div> : null}
			{aviso ? <div className="wdNota" onClick={() => setAviso(null)}>{aviso}</div> : null}
			{soloAlumno ? null : paginas(false)}
			{!data.rows.length ? <div className="wdTenue">{soloAlumno ? "Esta persona no tiene matrículas en la Limpieza de este plantel." : "Nada con estos filtros."}</div> : data.rows.map((r: any) => {
				const g = r.suggestion || {};
				const modoSug: Modo | null = g.decision || null;
				return (
					<div key={"k" + r.id} className={"wdCaso wdLimpia" + (abierto && abierto.id === r.id ? " wdCasoAbierto" : "")}>
						<div className="wdLimpCab">
							<div style={{ minWidth: 0 }}>
								<b>{r.name}</b>{r.statusId !== 1 ? <span className="wdChip wdChipFilo" style={{ marginLeft: 6 }}>{r.statusId === 3 && r.group?.role === "base" ? "Paga Completo · Base" : r.status}</span> : null}
								{r.priority && r.statusId === 1 ? <span className="wdChip wdChipOro" style={{ marginLeft: 6 }} title="Va primero en la lista">{r.priorityReason || "Prioridad"}</span> : null}
								<div className="wdTenue wdCorta" style={{ fontSize: 12 }}>{r.enrolledAt ? "Matriculada el " + fecha(r.enrolledAt) + " · " : ""}{r.courseName} · {r.identityCode}</div>
							</div>
							<div style={{ minWidth: 0 }}>
								<TiraDeMeses compacta sym={sym} meses={(r.strip || []).map((m: any) => ({ n: m.n, state: estadoTira(m), price: m.price, list: m.list }))} />
							</div>
							<div className="wdOpciones wdLimpVer">
								<button type="button" className="wdToggle" onClick={() => setVentana({ tipo: "perfil", row: r })}>Ver Perfil</button>
								<button type="button" className="wdToggle" onClick={() => setVentana({ tipo: "cuenta", row: r })}>Estado de Cuenta</button>
							</div>
						</div>
						<div className="wdSugs">{sugerencia(r)}{notasCortas(r)}</div>
						{data.canDecide && r.statusId === 1 ? (
							<div className="wdOpciones wdLimpAcc">
								{modoSug === "ia" || modoSug === "confirmar" ? <button type="button" className={"wdToggle wdToggleSug" + (sel(r, modoSug) ? " wdToggleOn" : "")} disabled={cargando === r.id} onClick={() => abrir(r, modoSug)}>Aplicar Sugerencia</button> : null}
								<button type="button" className={"wdToggle" + (sel(r, "otra") ? " wdToggleOn" : "")} disabled={cargando === r.id} onClick={() => abrir(r, "otra")}>Elegir Otra</button>
								<button type="button" className={"wdToggle" + (sel(r, "ninguna") ? " wdToggleOn" : "")} disabled={cargando === r.id} onClick={() => abrir(r, "ninguna")}>{modoSug === "ninguna" ? "Aplicar Sugerencia: Sin Promoción" : "Sin Promoción"}</button>
								{cargando === r.id ? <span className="wdTenue" style={{ fontSize: 13 }}>Calculando…</span> : null}
							</div>
						) : r.statusId !== 1 ? (
							<div className="wdOpciones wdLimpAcc"><button type="button" className="wdLink" onClick={() => abrir(r, null)}>{abierto && abierto.id === r.id ? "Ocultar" : "Ver lo Decidido"}</button></div>
						) : null}
						{abierto && abierto.id === r.id ? detalle() : null}
					</div>
				);
			})}
			{!soloAlumno && data.rows.length > 20 ? paginas(true) : null}
			{ventana && ventana.tipo === "perfil" ? <PerfilAlumnoModal studentId={ventana.row.studentId} campusId={campusId} onClose={() => setVentana(null)} /> : null}
			{ventana && ventana.tipo === "cuenta" ? <ExpedienteLimpieza campusId={campusId} cleanupId={ventana.row.id} studentId={ventana.row.studentId} nombre={ventana.row.name} onClose={() => setVentana(null)} /> : null}
		</>
	);
};

export default Limpieza;
